import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, CircularProgress, Button, Tabs, Tab, Grid, Paper, Rating } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import axios from 'axios';
import { Restaurant } from '../types';

interface Favorite {
  id: number;
  restaurant: Restaurant;
  created_at: string;
}

interface PaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Favorite[];
}

const FavoritesPage: React.FC = () => {
  const [favorites, setFavorites] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const { token } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        if (!token) {
          navigate('/login');
          return;
        }

        setLoading(true);
        const response = await axios.get<PaginatedResponse>('/api/auth/favorites/', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        const favoriteRestaurants = response.data.results.map((favorite: Favorite) => favorite.restaurant);
        setFavorites(favoriteRestaurants);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching favorites:', error);
        setLoading(false);
      }
    };

    fetchFavorites();
  }, [token, navigate]);

  const handleRestaurantClick = (restaurant: Restaurant) => {
    navigate(`/restaurant/${restaurant.id}`);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          My Favorites
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/add-restaurant')}
          sx={{ mb: 3 }}
        >
          Add New Restaurant
        </Button>

        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Favorites" />
          <Tab label="Added by Me" />
        </Tabs>

        {activeTab === 0 && (
          <Grid container spacing={3}>
            {favorites.length === 0 ? (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    You haven't added any restaurants to your favorites yet.
                  </Typography>
                </Paper>
              </Grid>
            ) : (
              favorites.map((restaurant) => (
                <Grid item xs={12} sm={6} md={4} key={restaurant.id}>
                  <Paper
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 6,
                      },
                    }}
                    onClick={() => handleRestaurantClick(restaurant)}
                  >
                    {restaurant.images && restaurant.images[0] && (
                      <Box
                        component="img"
                        src={restaurant.images[0].url}
                        alt={restaurant.name}
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                          borderRadius: 1,
                          mb: 2,
                        }}
                      />
                    )}
                    <Typography variant="h6" gutterBottom>
                      {restaurant.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Rating value={restaurant.average_rating || 0} precision={0.1} readOnly size="small" />
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        {restaurant.average_rating?.toFixed(1) || 'No ratings'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {restaurant.address}
                    </Typography>
                  </Paper>
                </Grid>
              ))
            )}
          </Grid>
        )}

        {activeTab === 1 && (
          <Grid container spacing={3}>
            {/* Added by Me tab content will be implemented later */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  You haven't added any restaurants yet.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default FavoritesPage; 